// An agent test's expectations (its checks) in words (ADR 0035): what `spec.expect` asks of an answer, as the Evaluations show it under a
// case and, after a run, each check with its verdict and what came back.

/** One check of an agent test (`spec.expect[]`), or its verdict in a result (`details.checks[]`). */
export interface EvalCheck {
  path: string;
  op: string;
  value?: unknown;
  expected?: unknown;
  ok?: boolean;
  actual?: unknown;
}

/** A pattern `/x/flags` as its words when it is a plain word or two; else the pattern itself. */
const patternWords = (v: unknown) => {
  const s = String(v ?? '');
  const m = /^\/(.*)\/[a-z]*$/s.exec(s);
  const body = m ? m[1]! : s;
  if (/^[\w .:-]+$/.test(body)) return body;
  // A real pattern reads as one: shortened, its alternatives as "a or b" when it is only that.
  if (/^[\w .:-]+(\|[\w .:-]+)+$/.test(body) && body.length <= 40) return body.split('|').join(' or ');
  return `/${body.length > 40 ? `${body.slice(0, 40)}…` : body}/`;
};

const seconds = (ms: unknown) => `${Math.round(Number(ms) / 1000)} s`;

/** What a check asks, in a few words ("answer has app_dashboard", "did not call save_screens"). */
export function checkText(c: EvalCheck): string {
  const v = c.value !== undefined ? c.value : c.expected;
  const p = c.path;
  if (p === '$.refused' && c.op === 'falsy') return 'not refused';
  if (p === '$.refused' && c.op === 'truthy') return 'refused';
  if (p === '$.answer' && c.op === 'min_length')
    return Number(v) <= 1 ? 'answer not empty' : `answer at least ${String(v)} characters`;
  if (p === '$.answer' && c.op === 'matches') return `answer has ${patternWords(v)}`;
  if (p === '$.answer' && c.op === 'not_matches') return `answer has no ${patternWords(v)}`;
  if (p === '$.answer' && c.op === 'contains') return `answer has ${String(v)}`;
  if (p === '$.answer' && c.op === 'not_contains') return `answer has no ${String(v)}`;
  if (p === '$.tool_names' && c.op === 'contains') return `called ${String(v)}`;
  if (p === '$.tool_names' && c.op === 'not_contains') return `did not call ${String(v)}`;
  if (p === '$.tool_names' && c.op === 'matches') return `called ${patternWords(v)}`;
  if (p === '$.tool_names' && c.op === 'not_matches') return `did not call ${patternWords(v)}`;
  if (p === '$.tool_call_count' && c.op === 'eq')
    return Number(v) === 0 ? 'called no tool' : `called ${String(v)} tools`;
  if (p === '$.duration_ms' && c.op === 'lte') return `answers within ${seconds(v)}`;
  if (p === '$.chart_count' && c.op === 'gte')
    return Number(v) <= 1 ? 'draws a chart' : `draws ${String(v)}+ charts`;
  if (p === '$.usage.cost_usd' && c.op === 'lte') return `costs at most $${String(v)}`;
  if (p === '$.usage.total_tokens' && c.op === 'lte')
    return `uses at most ${Number(v).toLocaleString('en-US')} tokens`;
  if (c.op === 'schema') return `${p.replace(/^\$\./, '')} matches its schema`;
  if (c.op === 'falsy' || c.op === 'truthy' || c.op === 'exists') return `${p.replace(/^\$\./, '')} ${c.op}`;
  return `${p.replace(/^\$\./, '')} ${c.op} ${typeof v === 'string' ? v : JSON.stringify(v)}`;
}

/** What came back for a failed check, shortened for one line. */
export function actualText(c: EvalCheck): string {
  const a = c.actual;
  const s = typeof a === 'string' ? a : JSON.stringify(a);
  return (s ?? 'nothing').replace(/\s+/g, ' ').slice(0, 160);
}
